import { Link } from "react-router-dom";
import { ArrowRight, Award } from "lucide-react";
import SectionReveal from "@/components/SectionReveal";
import useSeo from "@/hooks/useSeo";

const PIP_PORTRAIT =
  "https://images.leadconnectorhq.com/image/f_webp/q_80/r_1200/u_https://assets.cdn.filesafe.space/ZZ0MV02eR9AkUfkNW9rm/media/68918892600bc50615680821.png";

const CREDENTIALS = [
  "Bachelor of Arts / Bachelor of Teaching",
  "Positive Psychology Coach",
  "Menopause Coach",
  "Clinical Hypnotherapist & Strategic Psychotherapist",
  "NLP Masters",
];

export default function About() {
  useSeo({
    title: "About Pip Bennett — Strategic Life Coach & Clinical Hypnotherapist",
    description: "Pip Bennett is a clinical hypnotherapist and strategic life coach who works with capable, often outwardly successful people who are internally stuck.",
    canonical: "https://pipbennett.com/about",
  });
  return (
    <div data-testid="page-about">
      <section className="pt-16 md:pt-24 pb-20 md:pb-28 bg-gradient-to-b from-cream to-cream-soft">
        <div className="max-w-6xl mx-auto px-6 md:px-10 lg:px-14 grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          <SectionReveal className="lg:col-span-5">
            <img
              src={PIP_PORTRAIT}
              alt="Pip Bennett portrait"
              className="w-full rounded-[1.5rem] shadow-[0_30px_60px_-30px_rgba(74,93,79,0.3)]"
            />
          </SectionReveal>
          <SectionReveal delay={150} className="lg:col-span-7">
            <div className="eyebrow mb-5 text-sage">About Pip</div>
            <h1 className="font-serif-display text-4xl md:text-5xl lg:text-6xl text-ink leading-[1.05]">
              Strategic clarity, delivered with warmth.
            </h1>
            <div className="mt-10 space-y-6 text-ink-soft leading-relaxed text-[1.02rem]">
              <p>
                I'm Pip Bennett — a clinical hypnotherapist and strategic life coach who works with people who are
                capable, responsible, and often outwardly successful — yet internally stuck in hesitation, overthinking,
                or patterns that no longer make sense logically.
              </p>
              <p>
                My work isn't therapy, symptom management, or wellness-based support. It sits in a precise, specific
                place: the space between knowing what you want and actually doing it. That space where willpower stops
                working and deeper patterns take over.
              </p>
              <p>
                I've spent years training in the mechanics of behavioural change — not the platitudes. What I offer
                blends strategic life coaching with clinical hypnotherapy to address the patterns beneath your conscious
                effort, so clarity and decisiveness become sustainable rather than forced.
              </p>
              <p>
                I work primarily over Zoom, with people in midlife, menopause, and transitions where confidence has
                quietly eroded despite competence. My signature group experience, The Rewired Life, is available as a
                self-paced digital program — and once a year, a limited live cohort.
              </p>
            </div>

            <div className="mt-12">
              <div className="eyebrow mb-5 text-sage">Training &amp; Credentials</div>
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {CREDENTIALS.map((c) => (
                  <li key={c} className="flex items-center gap-3 text-ink">
                    <Award size={16} strokeWidth={1.5} className="text-gold" />
                    <span className="text-sm">{c}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-8 flex flex-wrap items-center gap-6" data-testid="about-credential-badges">
                <a
                  href="https://www.credly.com/badges/66f2e504-8afe-4ca0-ba5c-541cae1bd7c6/email"
                  target="_blank"
                  rel="noreferrer"
                  title="International Coaching Federation — Member"
                  className="block hover:opacity-80 transition-opacity"
                  data-testid="about-badge-icf"
                >
                  <img
                    src="/icf-member-badge.png"
                    alt="ICF — International Coaching Federation Member"
                    className="w-28 h-28 object-contain"
                  />
                </a>
              </div>
              <p className="mt-4 text-xs text-ink-soft italic">
                Additional credential badges coming soon.
              </p>
            </div>

            <div className="mt-12 flex flex-col sm:flex-row gap-4">
              <Link
                to="/the-menopause-invitation"
                className="inline-flex items-center gap-2 bg-sage hover:bg-sage-dark text-cream rounded-full px-8 py-4 font-medium tracking-wide transition-all"
                data-testid="about-cta-program"
              >
                Explore The Menopause Invitation
                <ArrowRight size={16} strokeWidth={1.8} />
              </Link>
              <Link
                to="/waitlist"
                className="inline-flex items-center gap-2 border border-sage text-sage hover:bg-sage hover:text-cream rounded-full px-8 py-4 font-medium tracking-wide transition-all"
                data-testid="about-cta-waitlist"
              >
                Join the 1:1 Waitlist
              </Link>
            </div>
          </SectionReveal>
        </div>
      </section>
    </div>
  );
}
